import { useCallback } from 'react';

import { trackEvent } from '@zep/logger/tracking';

import { type BuildQuizListItem } from '../../../form/schema/BuildQuizListSchema';
import { AI_BUILDER_CHAT_PLACEHOLDER } from '../constants';
import {
  appendSubmittedAiBuilderChoiceUserMessage,
  createSubmittedAiBuilderChoiceUserMessageId,
  getSubmittedAiBuilderChoiceOptions,
} from '../store/aiBuilderMessageState';
import { composeChoiceMessageContent } from '../store/aiBuilderMessageStream';
import {
  createAiBuilderClientRequestId,
  createMessageTurnRequest,
} from '../store/aiBuilderTurnRequests';
import { isAiBuilderUsageBlocked } from '../store/aiBuilderUsagePolicy';
import {
  type AiBuilderChatActions,
  type AiBuilderChatState,
  type AiBuilderMessage,
  type AiBuilderSubmitPromptOptions,
  type AiBuilderUserMessageRetryRequest,
} from '../store/types';
import { type useDispatchServerTurn } from '../store/useAiBuilderServerTurnDispatcher';
import { aiBuilderChatAdapter } from '../utils/aiBuilderChatAdapter';
import { type AiBuilderChatCopy } from '../utils/aiBuilderChatCopy';
import { trackAiBuilderUsageBlocked } from '../utils/aiBuilderChatTracking';
import { createChoiceTurnHints } from '../utils/aiBuilderTurnHints';
import { type AiBuilderTurnRequest } from '../utils/types';

type UpdateAiBuilderChatState = (
  updater: (prev: AiBuilderChatState) => AiBuilderChatState,
) => void;
type AiBuilderChoiceMessage = Extract<AiBuilderMessage, { kind: 'choice' }>;
type AiBuilderMutableRef<T> = { current: T };
type GetNextAiBuilderRequestId = () => number;
type IsAiBuilderTurnBusy = () => boolean;

type CreateTurnContextParams = {
  signal?: AbortSignal;
  sourceRefs?: string[];
  turnHints?: AiBuilderSubmitPromptOptions['turnHints'];
};

type UseAiBuilderSubmitChoiceParams = {
  copy: AiBuilderChatCopy;
  createTurnContext: (
    params?: CreateTurnContextParams,
  ) => AiBuilderTurnRequest['context'];
  dispatchServerTurn: ReturnType<typeof useDispatchServerTurn>;
  getNextRequestId: GetNextAiBuilderRequestId;
  isTurnBusy: IsAiBuilderTurnBusy;
  lastQuizListSnapshotRef: AiBuilderMutableRef<BuildQuizListItem[] | null>;
  getChatState: () => AiBuilderChatState;
  updateState: UpdateAiBuilderChatState;
};

export const useAiBuilderSubmitChoice = ({
  copy,
  createTurnContext,
  dispatchServerTurn,
  getNextRequestId,
  isTurnBusy,
  lastQuizListSnapshotRef,
  getChatState,
  updateState,
}: UseAiBuilderSubmitChoiceParams) => {
  return useCallback<AiBuilderChatActions['submitChoiceMessage']>(
    (messageId, responseText) => {
      if (isTurnBusy()) return;

      const { messages, usage } = getChatState();
      const selectedOptions = getSubmittedAiBuilderChoiceOptions({
        messages,
        messageId,
      });

      if (!selectedOptions) return;

      if (isAiBuilderUsageBlocked(usage)) {
        trackAiBuilderUsageBlocked(usage, 'choice');
        return;
      }

      // 라벨은 대화에 남길 사용자 발화 텍스트 전용이다 — 서버가 읽을 결정은
      // 선지의 canonical value 로만 전달한다 (createChoiceTurnHints).
      const selectedLabels = selectedOptions.map(option => option.label);
      const choiceMessage = messages.find(
        (message): message is AiBuilderChoiceMessage =>
          message.kind === 'choice' && message.id === messageId,
      );
      const choiceTurnHints = choiceMessage
        ? createChoiceTurnHints({
            message: choiceMessage,
            selectedOptions,
          })
        : undefined;

      const requestId = getNextRequestId();
      const clientRequestId = createAiBuilderClientRequestId();
      const userMessageId =
        createSubmittedAiBuilderChoiceUserMessageId(requestId);
      const responseTextValue = responseText?.trim() || undefined;
      const choiceMessageText = composeChoiceMessageContent({
        selectedLabels,
        responseText: responseTextValue,
      });
      const retryRequest: AiBuilderUserMessageRetryRequest = {
        clientRequestId,
        type: 'message',
        prompt: choiceMessageText,
        selectedQuizContext: null,
        sourceRefs: [],
        ...(choiceTurnHints ? { turnHints: choiceTurnHints } : {}),
      };

      updateState(prev => ({
        ...prev,
        draft: '',
        selectedFile: null,
        placeholder: AI_BUILDER_CHAT_PLACEHOLDER.default,
        messages: appendSubmittedAiBuilderChoiceUserMessage({
          messages: prev.messages,
          messageId,
          responseText,
          requestId,
          retry: retryRequest,
          selectedLabels,
        }),
      }));
      lastQuizListSnapshotRef.current = null;
      trackEvent('quiz_ai_builder_choice_submit', {
        selected_count: selectedLabels.length,
        has_response_text: Boolean(responseTextValue),
        session_id: getChatState().sessionId ?? '',
        client_request_id: clientRequestId,
      });

      dispatchServerTurn(
        aiBuilderChatAdapter.startTurn,
        signal =>
          createMessageTurnRequest({
            requestId,
            clientRequestId,
            clientCopy: copy,
            message: choiceMessageText,
            analyticsIntent: 'choice',
            context: createTurnContext({
              signal,
              ...(choiceTurnHints ? { turnHints: choiceTurnHints } : {}),
            }),
          }),
        { userMessageId },
      );
    },
    [
      copy,
      createTurnContext,
      dispatchServerTurn,
      getChatState,
      getNextRequestId,
      isTurnBusy,
      lastQuizListSnapshotRef,
      updateState,
    ],
  );
};
